import { useState, useCallback } from 'react';
import { Card } from '@/propertyai/components/ui/card';
import { Input } from '@/propertyai/components/ui/input';
import { Label } from '@/propertyai/components/ui/label';
import { Separator } from '@/propertyai/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/propertyai/components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

export default function MortgageCalculatorPage() {
  const [price, setPrice] = useState(450000);
  const [downPayment, setDownPayment] = useState(90000);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);
  const [taxRate, setTaxRate] = useState(1.2);
  const [insuranceRate, setInsuranceRate] = useState(0.5);

  const calculate = useCallback(() => {
    const loanAmount = price - downPayment;
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    let monthlyPI = 0;
    if (monthlyRate > 0) {
      monthlyPI = loanAmount * monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments));
    } else {
      monthlyPI = loanAmount / numPayments;
    }

    const monthlyTax = (price * taxRate / 100) / 12;
    const monthlyInsurance = (price * insuranceRate / 100) / 12;
    const pmi = (downPayment / Math.max(price, 1)) < 0.2 ? (loanAmount * 0.005) / 12 : 0;
    const total = monthlyPI + monthlyTax + monthlyInsurance + pmi;
    const totalInterest = monthlyPI * numPayments - loanAmount;

    return {
      monthlyPI: Math.round(monthlyPI),
      monthlyTax: Math.round(monthlyTax),
      monthlyInsurance: Math.round(monthlyInsurance),
      pmi: Math.round(pmi),
      total: Math.round(total),
      loanAmount: Math.round(loanAmount),
      totalInterest: Math.round(totalInterest),
      totalCost: Math.round(total * numPayments + downPayment),
      downPaymentPct: Math.round(downPayment / Math.max(price, 1) * 100),
    };
  }, [price, downPayment, rate, term, taxRate, insuranceRate]);

  const result = calculate();

  const pieData = [
    { name: 'Principal & Interest', value: result.monthlyPI, color: 'hsl(186, 72%, 33%)' },
    { name: 'Property Tax', value: result.monthlyTax, color: 'hsl(222, 47%, 30%)' },
    { name: 'Insurance', value: result.monthlyInsurance, color: 'hsl(34, 70%, 55%)' },
    ...(result.pmi > 0 ? [{ name: 'PMI', value: result.pmi, color: 'hsl(160, 45%, 38%)' }] : []),
  ];

  // Generate amortization schedule summary (yearly)
  const amortSchedule = [];
  let balance = result.loanAmount;
  const monthlyRate = rate / 100 / 12;
  for (let year = 1; year <= Math.min(term, 30); year++) {
    let yearPrincipal = 0, yearInterest = 0;
    for (let m = 0; m < 12; m++) {
      if (balance <= 0) break;
      const interest = balance * monthlyRate;
      const principal = result.monthlyPI - interest;
      yearPrincipal += principal;
      yearInterest += interest;
      balance -= principal;
    }
    amortSchedule.push({
      year: `Y${year}`,
      principal: Math.round(yearPrincipal),
      interest: Math.round(yearInterest),
      balance: Math.max(0, Math.round(balance)),
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-semibold font-['Space_Grotesk'] tracking-tight">Mortgage Calculator</h1>
          <p className="text-muted-foreground mt-2">Calculate your monthly mortgage payment and see a complete breakdown</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Inputs */}
          <Card className="rounded-2xl border border-border bg-background p-6">
            <h2 className="font-semibold mb-4">Loan Details</h2>
            <div className="space-y-4">
              <div>
                <Label>Home Price</Label>
                <Input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="mt-1.5 rounded-xl tabular-nums" data-testid="mortgage-page-price" />
                <input type="range" min={100000} max={2000000} step={10000} value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-full mt-2 accent-primary" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Down Payment</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{result.downPaymentPct}%</span>
                </div>
                <Input type="number" value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))} className="mt-1.5 rounded-xl tabular-nums" data-testid="mortgage-page-down" />
                <input type="range" min={0} max={price * 0.5} step={5000} value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))} className="w-full mt-2 accent-primary" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Interest Rate (%)</Label>
                  <Input type="number" step={0.1} value={rate} onChange={(e) => setRate(Number(e.target.value))} className="mt-1.5 rounded-xl tabular-nums" data-testid="mortgage-page-rate" />
                </div>
                <div>
                  <Label>Loan Term (years)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => setTerm(15)} className={`flex-1 py-2 rounded-xl text-sm font-medium border ${term === 15 ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}>15 yr</button>
                    <button onClick={() => setTerm(30)} className={`flex-1 py-2 rounded-xl text-sm font-medium border ${term === 30 ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}>30 yr</button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Property Tax Rate (%)</Label>
                  <Input type="number" step={0.1} value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} className="mt-1.5 rounded-xl tabular-nums" />
                </div>
                <div>
                  <Label>Insurance Rate (%)</Label>
                  <Input type="number" step={0.1} value={insuranceRate} onChange={(e) => setInsuranceRate(Number(e.target.value))} className="mt-1.5 rounded-xl tabular-nums" />
                </div>
              </div>
            </div>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            <Card className="rounded-2xl border border-border bg-background p-6">
              <div className="text-center mb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Payment</p>
                <p className="text-4xl font-semibold tabular-nums font-['Space_Grotesk'] mt-1" data-testid="mortgage-page-result">
                  ${result.total.toLocaleString()}
                </p>
              </div>

              {/* Pie Chart */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-28 h-28 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={28} outerRadius={48} dataKey="value" stroke="none">
                        {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                      </Pie>
                      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {pieData.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                        <span className="text-xs text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-medium tabular-nums text-xs">${item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Loan Amount</span>
                  <span className="font-medium tabular-nums">${result.loanAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Interest</span>
                  <span className="font-medium tabular-nums">${result.totalInterest.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Cost</span>
                  <span className="font-semibold tabular-nums">${result.totalCost.toLocaleString()}</span>
                </div>
              </div>
            </Card>

            {/* Amortization chart */}
            <Card className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-semibold text-sm mb-3">Amortization Schedule</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={amortSchedule.filter((_, i) => i % (term > 15 ? 3 : 1) === 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                  <Bar dataKey="principal" fill="hsl(186, 72%, 33%)" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="interest" fill="hsl(34, 70%, 55%)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-4 mt-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'hsl(186, 72%, 33%)' }} />
                  Principal
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'hsl(34, 70%, 55%)' }} />
                  Interest
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

